import React from 'react';
import ddt from '@wix/da-ddt';
import { SessionContext } from '../publicSession';

const filteredErrors: { filter: RegExp; component?: string[] }[] = [
  { filter: /Failed to execute 'removeChild'/i }, // Adblocker
  { filter: /Failed to execute 'insertBefore'/i }, // Adblocker
  { filter: /The object can not be found here/i }, // Strange chromeIOS
  { filter: /IntersectionObserver/i }, // Bots
  { filter: /ResizeObserver/i }, // Bots
  { filter: /Loading chunk/i }, // Webpack net error
  { filter: /Loading CSS chunk/i }, // Webpack net error
  { filter: /access dead object/i }, // FF addon error
  { filter: /Permission denied to access object/i }, // FF addon error
  { filter: /invariant=306/i, component: ['ChatInit'] }, // Chunk loading failed, due to loading on pageload
  { filter: /Intl is not defined|Can't find variable: Intl/i }, // Crappy browsers
  { filter: /Object\.entries is not a function/i }, // Bots
  { filter: /Object\.values is not a function/i }, // Bots
  { filter: /not a child of this node/i }, // Adblocker
  { filter: /proxyDocumentStyleSheets/i }, // Extension
  { filter: /Failed to construct 'Worker'/i }, // Ads
  { filter: /cannot be created in a document with origin/i }, // Proxies trying to use replaceState
  { filter: /Blocked attempt to use history/i }, // Proxies trying to use replaceState
  { filter: /Object.fromEntries/i }, // Unsupported browsers
  { filter: /Failed to register a ServiceWorker for scope/i }, // Proxy dying
];

const stackFilters = [/user-script/, /chrome-extension/];

export function shouldLogError(error) {
  let shouldLog = true;
  for (const fe of filteredErrors) {
    if (fe.filter.test(error && error.message)) {
      if (
        Array.isArray(fe.component) &&
        !fe.component?.includes(this.props.debugComponent)
      ) {
        continue; // allows filtering for specific components
      }
      shouldLog = false;
      break;
    }
  }

  for (const sf of stackFilters) {
    if (sf.test(error && error.stack)) {
      shouldLog = false;
      if (/user-script|chrome-extension/.test(error && error.stack)) {
        alert('Your user script or extension crashed, please reload the page.');
      }
    }
  }

  return shouldLog;
}

export interface Props {
  humanError?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  hideOnFail?: boolean;
  debugComponent: string;
  sessionContext?: any;
}

export interface State {
  isError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  static getDerivedStateFromError() {
    return { isError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (
      process.env.NODE_ENV === 'development' &&
      errorInfo &&
      errorInfo.componentStack
    ) {
      // eslint-disable-next-line no-console
      console.error(errorInfo.componentStack); // tslint:disable-line
    }

    if (!shouldLogError(error)) {
      return;
    }

    ddt.alert(
      'fatal',
      `Error Boundary Caught: ${(error && error.message) || 'unknown'}`,
      ['fatal', this.props.debugComponent],
      {
        stacktrace: error && error.stack,
        component: this.props.debugComponent,
        session: this.props.sessionContext,
        url: window.location.href,
      }
    );

    if (process.env.NODE_ENV === 'test') {
      throw error;
    }
  }

  render() {
    if (this.state && this.state.isError) {
      if (this.props.hideOnFail) {
        return null;
      }

      return (
        <div
          className={this.props.className}
          style={{
            font: 'var(--f-16-reg)',
            border: '4px solid var(--g-error-primary)',
            borderRadius: '4px',
            padding: '20px',
            color: 'var(--g-error-primary)',
            textAlign: 'center',
            ...this.props.style,
          }}
        >
          {this.props.humanError && <p>{this.props.humanError}</p>}
          {!this.props.humanError && (
            <>
              <p style={{ font: 'var(--f-14-reg)' }}>E_GURU_MEDITATION</p>
              <h3 style={{ font: 'var(--h6)', marginBottom: '10px' }}>
                Something&apos;s gone wrong on our end
              </h3>
              <p>
                We&apos;re having a technical issue. Try reloading the page.
                <br />
                If this keeps happening, please contact Customer Support.
              </p>
            </>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const withContext: React.FC<Props> = props => (
  <SessionContext.Consumer>
    {contextProps => <ErrorBoundary {...props} sessionContext={contextProps} />}
  </SessionContext.Consumer>
);
withContext.displayName = 'ErrorBoundary';

export default withContext;
